/**
 * SITE PÚBLICO – Página de Painéis Solares.
 * Página do site público (Electro Salir) apenas.
 * URL: /paineis-solares
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Sun,
  Home,
  Building2,
  Wrench,
  TrendingUp,
  ChevronDown,
  Phone,
  MapPin,
  Zap,
} from 'lucide-react';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';

/*
  SEO – Meta description: usada no <meta name="description"> (definida em useEffect).
  Sugestões de alt text para imagens futuras:
  - Hero/banner: "Instalação de painéis solares em Salir e Algarve – Electro Salir, energia solar e poupança energética"
  - Painéis no telhado: "Painéis solares fotovoltaicos instalados no Algarve pela Electro Salir"
  - Equipa/trabalho: "Técnicos da Electro Salir a instalar painéis solares em Salir"
  - Casa com painéis: "Casa em Salir ou Algarve com instalação solar da Electro Salir"
*/

const META_DESCRIPTION =
  'Instalação de painéis solares em Salir e Algarve. Electro Salir – 35+ anos de experiência local. Poupança energética, orçamentos sem compromisso. 289 489 848 | 917 869 307.';

const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Vale a pena instalar painéis solares em Salir?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sim. O Algarve tem muitas horas de sol por ano, o que torna a produção de energia solar muito eficiente. Em Salir e na região, os painéis fotovoltaicos costumam ter um bom retorno: redução na factura da electricidade, maior autonomia energética e valorização do imóvel. A Electro Salir analisa o seu caso e indica se o investimento compensa no seu contexto.',
        },
      },
      {
        '@type': 'Question',
        name: 'Quanto posso poupar com painéis solares?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A poupança depende do consumo, da dimensão da instalação e da orientação do telhado. Uma instalação bem dimensionada pode reduzir de forma significativa a factura da electricidade ao longo do ano. A poupança acumula a médio e longo prazo. Fazemos uma análise personalizada e estimamos a poupança para o seu caso.',
        },
      },
      {
        '@type': 'Question',
        name: 'Os painéis precisam de manutenção?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sim, mas é relativamente simples. Recomendamos limpeza periódica dos painéis (poeira, folhas) e uma verificação técnica de tempos a tempos. A Electro Salir oferece acompanhamento e manutenção para instalações em Salir e no Algarve.',
        },
      },
      {
        '@type': 'Question',
        name: 'Quanto tempo demora a instalação?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Depende da dimensão do projecto e dos trâmites (licenciamento, quando aplicável). Uma instalação residencial típica pode ficar concluída em poucos dias de trabalho. No orçamento indicamos sempre um prazo estimado.',
        },
      },
    ],
  };

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Electro Salir',
    description: 'Instalação de painéis solares em Salir e Algarve. Empresa local com mais de 35 anos de experiência.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Rua Manuel Dourado Eusébio N.º 34 A',
      addressLocality: 'Salir',
      postalCode: '8100-202',
      addressRegion: 'Algarve',
      addressCountry: 'PT',
    },
    telephone: ['+351289489848', '+351917869307'],
    email: 'electrosalir@sapo.pt',
    areaServed: { '@type': 'Place', name: 'Salir e Algarve' },
};

export default function SitePaineisSolares() {
  useEffect(() => {
    const prevTitle = document.title;
    const prevMeta = document.querySelector('meta[name="description"]')?.getAttribute('content');
    document.title = 'Instalação de Painéis Solares em Salir e Algarve | Electro Salir';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', META_DESCRIPTION);

    const s1 = document.createElement('script');
    s1.type = 'application/ld+json';
    s1.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(s1);
    const s2 = document.createElement('script');
    s2.type = 'application/ld+json';
    s2.textContent = JSON.stringify(localBusinessSchema);
    document.head.appendChild(s2);

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevMeta) metaDesc.setAttribute('content', prevMeta);
      s1.remove();
      s2.remove();
    };
  }, []);

  const heroBg = `${process.env.PUBLIC_URL || ''}/hero-background.png`;

  return (
    <div className="min-h-screen bg-gestao-background text-slate-800">
      <SiteHeader />
      <main className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gestao-primary font-semibold hover:underline mb-8"
          >
            <ArrowLeft size={18} />
            Voltar ao início
          </Link>

          {/* Hero com imagem para SEO – alt: "Instalação de painéis solares em Salir e Algarve – Electro Salir, energia solar e poupança energética" */}
          <header
            className="relative rounded-2xl overflow-hidden mb-12 bg-slate-800"
            style={{ minHeight: '200px' }}
          >
            <img
              src={heroBg}
              alt="Instalação de painéis solares em Salir e Algarve – Electro Salir, energia solar e poupança energética"
              className="absolute inset-0 w-full h-full object-cover opacity-60"
            />
            <div className="relative z-10 p-8 sm:p-12 flex flex-col justify-center">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 drop-shadow-md">
                Instalação de Painéis Solares em Salir e Algarve | Electro Salir
              </h1>
              <p className="text-lg text-slate-200 max-w-2xl">
                Empresa local com mais de 35 anos de experiência. Especialistas em poupança
                energética e soluções solares para Salir e todo o Algarve – com a confiança de quem
                conhece a região.
              </p>
            </div>
          </header>

          {/* Introdução */}
          <section className="mb-16">
            <p className="text-lg text-slate-600 leading-relaxed">
              A Electro Salir actua em Salir e no Algarve há mais de 35 anos. A instalação de
              painéis solares é uma das nossas áreas de trabalho: ajudamos particulares e empresas
              a reduzir a factura da electricidade e a valorizar o imóvel, com soluções pensadas
              para o clima e para as necessidades locais. Somos a sua referência local em energia
              solar – quem somos, onde actuamos e porque somos a escolha certa em Salir.
            </p>
          </section>

          {/* H2 – Serviços de painéis solares (mesma estrutura que página AC) */}
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">
              Serviços de painéis solares
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <div className="w-12 h-12 bg-gestao-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Sun size={24} className="text-gestao-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Instalação</h3>
                <p className="text-slate-600 text-sm">
                  Projectamos e instalamos sistemas fotovoltaicos adaptados ao seu consumo e à
                  orientação do telhado. Trabalhamos com equipamentos de qualidade e cumprimos as
                  normas em vigor.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <div className="w-12 h-12 bg-gestao-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Wrench size={24} className="text-gestao-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Manutenção</h3>
                <p className="text-slate-600 text-sm">
                  Limpeza dos painéis, verificação do sistema e suporte técnico. A manutenção
                  regular mantém a produção e prolonga a vida da instalação em Salir e região.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <div className="w-12 h-12 bg-gestao-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp size={24} className="text-gestao-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Acompanhamento</h3>
                <p className="text-slate-600 text-sm">
                  Oferecemos acompanhamento das instalações que montamos. Para que a sua
                  instalação solar continue a produzir o esperado ao longo dos anos.
                </p>
              </div>
            </div>
          </section>

          {/* H2 – Tipos de painéis solares (espelho de "Tipos de ar-condicionado" na página AC) */}
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">
              Tipos de painéis solares
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Fotovoltaico</h3>
                <p className="text-slate-600">
                  Converte a luz solar em electricidade para autoconsumo ou venda à rede. É o tipo
                  mais comum em habitações e empresas no Algarve. Dimensionamos o sistema em função
                  do seu consumo e da orientação do telhado.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Térmico</h3>
                <p className="text-slate-600">
                  Usa o sol para aquecer água (água quente sanitária ou apoio ao aquecimento).
                  Solução eficaz para reduzir o consumo de gás ou electricidade no aquecimento de
                  água, muito utilizada em moradias na região.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Híbrido</h3>
                <p className="text-slate-600">
                  Combina produção de electricidade e aquecimento de água no mesmo painel. Ideal
                  quando o espaço no telhado é limitado e se pretende maximizar o aproveitamento
                  solar. Avaliamos a viabilidade no seu caso.
                </p>
              </div>
            </div>
          </section>

          {/* H2 – Poupança e eficiência energética (mesma estrutura que AC – Consumo e eficiência) */}
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">
              Poupança e eficiência energética
            </h2>
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex gap-4 mb-4">
                <div className="w-12 h-12 bg-gestao-primary/10 rounded-lg flex items-center justify-center shrink-0">
                  <Zap size={24} className="text-gestao-primary" />
                </div>
                <div>
                  <p className="text-slate-600 leading-relaxed">
                    A poupança com painéis solares depende do consumo, da dimensão da instalação e da
                    orientação do telhado. No Algarve, com muitas horas de sol, os sistemas bem
                    dimensionados costumam ter um retorno atractivo: a factura da electricidade
                    reduz e a poupança acumula a médio e longo prazo. O custo da instalação varia
                    com a potência, o tipo de painéis e a complexidade da montagem. Na Electro Salir
                    fazemos um estudo do seu caso e apresentamos um orçamento claro, sem compromisso.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* H2 – Casas e empresas (mesma estrutura que página AC) */}
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">
              Casas e empresas
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 border border-slate-200 flex gap-4">
                <div className="w-12 h-12 bg-gestao-primary/10 rounded-lg flex items-center justify-center shrink-0">
                  <Home size={24} className="text-gestao-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Particulares</h3>
                  <p className="text-slate-600 text-sm">
                    Para a sua casa em Salir ou no Algarve, soluções adaptadas ao consumo e ao
                    telhado: dimensionamento adequado, instalação e apoio em todo o processo, para
                    usufruir da energia solar com tranquilidade.
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200 flex gap-4">
                <div className="w-12 h-12 bg-gestao-primary/10 rounded-lg flex items-center justify-center shrink-0">
                  <Building2 size={24} className="text-gestao-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Empresas</h3>
                  <p className="text-slate-600 text-sm">
                    Para comércio, oficinas, armazéns ou outros negócios na região: instalações
                    solares que ajudam a reduzir custos operacionais e a reforçar a imagem de
                    sustentabilidade. Planos à medida.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* H2 – Perguntas Frequentes */}
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">
              Perguntas Frequentes
            </h2>
            <div className="space-y-4">
              <details className="bg-white rounded-xl border border-slate-200 overflow-hidden group">
                <summary className="p-6 cursor-pointer list-none flex items-center justify-between font-semibold text-slate-900 hover:bg-slate-50 transition-colors">
                  Vale a pena instalar painéis solares em Salir?
                  <ChevronDown size={20} className="text-slate-400 group-open:rotate-180 transition-transform shrink-0 ml-2" />
                </summary>
                <div className="px-6 pb-6 text-slate-600 border-t border-slate-100 pt-4">
                  Sim. O Algarve tem muitas horas de sol por ano, o que torna a produção de energia
                  solar muito eficiente. Em Salir e na região, os painéis fotovoltaicos costumam ter
                  um bom retorno: redução na factura da electricidade, maior autonomia energética e
                  valorização do imóvel. A Electro Salir analisa o seu caso e indica se o
                  investimento compensa no seu contexto.
                </div>
              </details>
              <details className="bg-white rounded-xl border border-slate-200 overflow-hidden group">
                <summary className="p-6 cursor-pointer list-none flex items-center justify-between font-semibold text-slate-900 hover:bg-slate-50 transition-colors">
                  Quanto posso poupar?
                  <ChevronDown size={20} className="text-slate-400 group-open:rotate-180 transition-transform shrink-0 ml-2" />
                </summary>
                <div className="px-6 pb-6 text-slate-600 border-t border-slate-100 pt-4">
                  A poupança depende do consumo, da dimensão da instalação e da orientação do
                  telhado. Em média, uma instalação bem dimensionada pode reduzir de forma
                  significativa a factura da electricidade ao longo do ano. A poupança acumula a
                  médio e longo prazo e ajuda a amortizar o investimento. Fazemos uma análise
                  personalizada e estimamos a poupança para o seu caso.
                </div>
              </details>
              <details className="bg-white rounded-xl border border-slate-200 overflow-hidden group">
                <summary className="p-6 cursor-pointer list-none flex items-center justify-between font-semibold text-slate-900 hover:bg-slate-50 transition-colors">
                  Os painéis precisam de manutenção?
                  <ChevronDown size={20} className="text-slate-400 group-open:rotate-180 transition-transform shrink-0 ml-2" />
                </summary>
                <div className="px-6 pb-6 text-slate-600 border-t border-slate-100 pt-4">
                  Sim, mas é relativamente simples. Recomendamos limpeza periódica dos painéis
                  (poeira, folhas) e uma verificação técnica de tempos a tempos para garantir o bom
                  funcionamento. A Electro Salir oferece acompanhamento e manutenção para
                  instalações em Salir e no Algarve, para que possa usufruir da sua instalação com
                  tranquilidade.
                </div>
              </details>
              <details className="bg-white rounded-xl border border-slate-200 overflow-hidden group">
                <summary className="p-6 cursor-pointer list-none flex items-center justify-between font-semibold text-slate-900 hover:bg-slate-50 transition-colors">
                  Quanto tempo demora a instalação?
                  <ChevronDown size={20} className="text-slate-400 group-open:rotate-180 transition-transform shrink-0 ml-2" />
                </summary>
                <div className="px-6 pb-6 text-slate-600 border-t border-slate-100 pt-4">
                  Depende da dimensão do projecto e dos trâmites (licenciamento, quando aplicável).
                  Uma instalação residencial típica pode ficar concluída em poucos dias de trabalho.
                  Para projectos maiores ou com procedimentos administrativos, o prazo pode ser mais
                  longo. No orçamento indicamos sempre um prazo estimado e acompanhamos todo o
                  processo.
                </div>
              </details>
            </div>
          </section>

          {/* H2 – Contacto */}
          <section className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">Contacto</h2>
            <div className="bg-gestao-primary text-white rounded-2xl p-8 sm:p-10">
              <p className="text-lg mb-6 text-white/95">
                Quer saber quanto pode poupar com painéis solares em Salir ou no Algarve? Peça um
                orçamento sem compromisso. A Electro Salir está na região há mais de 35 anos – estamos
                perto de si e prontos para ajudar com a sua instalação solar.
              </p>
              <div className="flex flex-wrap gap-4 sm:gap-6">
                <a
                  href="tel:+351289489848"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gestao-primary font-semibold rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <Phone size={20} />
                  289 489 848
                </a>
                <a
                  href="tel:+351917869307"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gestao-primary font-semibold rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <Phone size={20} />
                  917 869 307
                </a>
              </div>
              <div className="mt-6 flex items-start gap-3 text-white/90">
                <MapPin size={20} className="shrink-0 mt-0.5" />
                <span>
                  Rua Manuel Dourado Eusébio N.º 34 A, Salir, 8100-202 Portugal
                </span>
              </div>
              <Link
                to="/contactos"
                className="inline-block mt-6 text-white font-semibold underline hover:no-underline"
              >
                Ver página de contactos →
              </Link>
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
